import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import {
  MatAutocompleteTrigger,
  MatAutocompleteSelectedEvent
} from '@angular/material/autocomplete';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { optionName } from '@aid/shared/utils/object-fields';

/**
 * This component allows you to search and call the api
 * for values
 */

@Component({
  selector: 'aid-autocomplete-select',
  template: `
    <mat-form-field class="full-width" color="accent">
      @if (label && applyPipes) {
        <mat-label>{{ label | translate | titlecase }}</mat-label>
      }
      @if (label && !applyPipes) {
        <mat-label>{{ label }}</mat-label>
      }

      <input
        matInput
        #input
        [matAutocomplete]="auto"
        (input)="onInputChanged(input.value)"
        [required]="isRequired"
        [formControl]="control"
      />
      @if (loading) {
        <mat-spinner class="select-search-spinner" diameter="16"> </mat-spinner>
      }
      @if (filterControl.value && !loading) {
        <button
          mat-icon-button
          (click)="onReset()"
          [disabled]="control.disabled"
          class="select-search-clear"
        >
          <mat-icon svgIcon="close"></mat-icon>
        </button>
      }

      <mat-autocomplete
        #auto="matAutocomplete"
        (opened)="onOpen()"
        (optionSelected)="handler($event)"
        [displayWith]="displayWith"
      >
        @if (!options || options.length === 0) {
          <mat-option>
            {{ 'no-items-found' | translate | sentancecase }}
          </mat-option>
        }
        @for (option of options; track option) {
          <mat-option [value]="option" data-cy="autocomplete-select">
            @if (useNameForOptionsInsteadOfDisplayFn) {
              {{ optionName(option) }}
            }
            @if (!useNameForOptionsInsteadOfDisplayFn) {
              {{ displayFn(option) }}
            }
          </mat-option>
        }
      </mat-autocomplete>

      @if (control.hasError('required')) {
        <mat-error>
          {{ label || 'this-field' | translate | titlecase }}
          {{ 'is-required' | translate }}
        </mat-error>
      }
    </mat-form-field>
  `,
  styleUrls: ['./autocomplete-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AutocompleteSelectComponent<T = unknown>
  implements OnInit, AfterViewInit, OnDestroy {
  constructor() {}
  /**
   * Input label
   */
  @Input() label?: string;
  @Input() applyPipes = true;
  @Input() useNameForOptionsInsteadOfDisplayFn = false;

  @Input() options?: T[] | null;
  @Input('autofocus') set _autofocus(autofocus: boolean) {
    if (autofocus) {
      this.focus = true;
      this.focusInput();
    }
  }

  /**
   * Parent Form control passed
   */
  @Input({ required: true }) control = new UntypedFormControl();

  @Input() loading?: boolean | null;

  @Output() search = new EventEmitter<string>();
  @Output() open = new EventEmitter();

  @ViewChild(MatAutocompleteTrigger)
  trigger?: MatAutocompleteTrigger;

  @ViewChild('input') input?: ElementRef<HTMLInputElement>;

  filterControl = new UntypedFormControl();
  value: unknown = null;
  focus = false;

  private ngUnsubscribe = new Subject<void>();
  private lastSearch: string | null = null;

  @Input() displayFn: (item: T) => string | null | undefined = item =>
    this.optionName(item);
  readonly displayWith = (item: T): string => this.displayFn(item) ?? '';

  ngOnInit() {
    this.value = this.control.value ? this.control.value : null;

    this.filterControl.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe), debounceTime(400))
      .subscribe(value => {
        const search = value ? value.toLowerCase() : value;
        if (this.lastSearch !== search) {
          this.lastSearch = search;
          this.search.emit(search);
        }
      });

    this.control.valueChanges.subscribe(value => {
      if (this.useNameForOptionsInsteadOfDisplayFn) {
        // quick fix, used only in raw-data-table
        this.inputElement.focus();
      }
      this.value = value ? this.displayFn(value) : null;
      this.focusInput();
      if (value === null) {
        this.inputElement.value = '';
      }
    });
  }

  ngAfterViewInit() {
    this._subscribeToClosingActions();
  }

  onOpen() {
    this.open.emit();
  }

  private focusInput() {
    if (!this.focus) {
      return;
    }
    setTimeout(() => {
      this.inputElement.focus();
    }, 100);
  }

  onInputChanged(search: string) {
    this.filterControl.setValue(search);
  }

  onReset() {
    this.inputElement.value = '';
    this.onInputChanged('');
  }

  handler(event: MatAutocompleteSelectedEvent): void {
    this.value = this.displayFn(event.option.value);
    this.control.setValue(event.option.value);
  }

  get isRequired(): boolean {
    if (this.control.validator) {
      const validator = this.control.validator({} as AbstractControl);
      if (validator && validator.required) {
        return true;
      }
    }

    return false;
  }

  private _subscribeToClosingActions(): void {
    this.autocompleteTrigger.panelClosingActions
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(e => {
        if (!e || !e.source) {
          this.filterControl.setValue(null);
          this.value = null;
          this.control.setValue(null);
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  closePanel() {
    this.autocompleteTrigger.closePanel();
  }

  optionName(option: T): string {
    return optionName(option);
  }

  private get inputElement(): HTMLInputElement {
    if (!this.input) {
      throw new Error('aid-autocomplete-select: the input is not rendered.');
    }
    return this.input.nativeElement;
  }

  private get autocompleteTrigger(): MatAutocompleteTrigger {
    if (!this.trigger) {
      throw new Error('aid-autocomplete-select: the trigger is not rendered.');
    }
    return this.trigger;
  }
}
